import { useTranslation } from "react-i18next";
import type { StoredNotification } from "../types";

/** Título + corpo renderizados de um tipo de notificação. */
export interface NotificationText {
  title: string;
  body: string | null;
}

/**
 * Hook de renderização type-driven (ADR-0028): o servidor central NÃO devolve
 * título/corpo prontos, só `type` + `payload`. Aqui montamos o texto a partir do
 * i18n (`backoffice:notifications.types.<tipo>.{title,body}`), interpolando os
 * campos do payload. Retorna um formatador estável para `pt-BR`/`en-US`.
 */
export function useNotificationFormatter(): (n: StoredNotification) => NotificationText {
  const { t } = useTranslation("backoffice");

  return (n: StoredNotification): NotificationText => {
    switch (n.type) {
      case "AdminRoleChanged":
        return {
          title: t("notifications.types.AdminRoleChanged.title", { role: n.payload.role }),
          body: t("notifications.types.AdminRoleChanged.body", { role: n.payload.role }),
        };
      case "AdminTwoFactorReset":
        return {
          title: t("notifications.types.AdminTwoFactorReset.title"),
          body: t("notifications.types.AdminTwoFactorReset.body"),
        };
      case "AdminAccountStatusChanged":
        return {
          title: t("notifications.types.AdminAccountStatusChanged.title", { status: n.payload.status }),
          body: t("notifications.types.AdminAccountStatusChanged.body", { status: n.payload.status }),
        };
      case "SupportTicketOpened":
        return {
          title: t("notifications.types.SupportTicketOpened.title", { code: n.payload.code }),
          body: t("notifications.types.SupportTicketOpened.body", {
            requester: n.payload.requesterName,
            subject: n.payload.subject,
          }),
        };
      case "SupportTicketMessageAdded":
        return {
          title: t("notifications.types.SupportTicketMessageAdded.title", { code: n.payload.code }),
          body: t("notifications.types.SupportTicketMessageAdded.body", { subject: n.payload.subject }),
        };
    }
  };
}

/** Rota associada à notificação. Chamados levam ao detalhe; as demais ao perfil. */
export function notificationRoute(n: StoredNotification): string {
  switch (n.type) {
    case "AdminRoleChanged":
    case "AdminTwoFactorReset":
    case "AdminAccountStatusChanged":
      return "/profile";
    case "SupportTicketOpened":
    case "SupportTicketMessageAdded":
      return `/support/tickets/${n.payload.ticketId}`;
  }
}
